import { useEffect, useRef } from 'react'
import L from 'leaflet'
import 'leaflet/dist/leaflet.css'

// Fix for default markers in React
delete (L.Icon.Default.prototype as any)._getIconUrl
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
})

interface ProjectLocation {
  id: string
  name: string
  address: string
  coordinates: [number, number] // [lng, lat]
  projectType: string
  description?: string
  year?: string
}

interface VancouverMapProps {
  className?: string
  projects?: ProjectLocation[]
}

// Default empty state - will be replaced by admin data
const defaultProjects: ProjectLocation[] = []

export default function VancouverMap({ className = '', projects = defaultProjects }: VancouverMapProps) {
  const mapRef = useRef<HTMLDivElement>(null)
  const mapInstanceRef = useRef<L.Map | null>(null)

  useEffect(() => {
    if (!mapRef.current) return

    // Initialize map centered on Vancouver
    const map = L.map(mapRef.current, {
      center: [49.2827, -123.1207], // Vancouver coordinates
      zoom: 12,
      zoomControl: false,
      scrollWheelZoom: false,
      doubleClickZoom: false,
      boxZoom: false,
      keyboard: false,
      dragging: false,
      touchZoom: false
    })

    // Add black and white map tiles
    L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
      attribution: '',
      maxZoom: 19,
      className: 'grayscale-map'
    }).addTo(map)

    // Create simple black pin icon
    const createPinIcon = () => {
      return L.divIcon({
        className: 'pin-marker',
        html: `
          <div style="
            background-color: #000000;
            width: 20px;
            height: 20px;
            border-radius: 50%;
            border: 3px solid #ffffff;
            box-shadow: 0 2px 6px rgba(0,0,0,0.4);
            display: flex;
            align-items: center;
            justify-content: center;
            color: white;
            font-weight: bold;
            font-size: 12px;
            transition: transform 0.2s ease;
          ">
            •
          </div>
        `,
        iconSize: [20, 20],
        iconAnchor: [10, 10]
      })
    }

    // Add markers for each project
    projects.forEach((project) => {
      const marker = L.marker(project.coordinates, {
        icon: createPinIcon()
      }).addTo(map)

      // Simple popup with just project name
      marker.bindPopup(`
        <div class="p-2 min-w-[150px]">
          <h3 class="font-bold text-sm text-gray-900">${project.name}</h3>
          <p class="text-xs text-gray-600 mt-1">${project.address}</p>
        </div>
      `, {
        maxWidth: 200,
        className: 'simple-popup'
      })
    })

    // Store map instance
    mapInstanceRef.current = map

    // Cleanup function
    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove()
        mapInstanceRef.current = null
      }
    }
  }, [projects])

  return (
    <div className={`relative ${className}`}>
      <div 
        ref={mapRef} 
        className="w-full h-full min-h-[500px] rounded-xl shadow-xl border border-gray-200"
        style={{ zIndex: 1 }}
      />
      
      {/* Custom CSS for black and white styling and static map */}
      <style>{`
        .grayscale-map {
          filter: grayscale(100%) contrast(1.2);
        }
        .simple-popup .leaflet-popup-content-wrapper {
          border-radius: 8px;
          box-shadow: 0 4px 12px rgba(0,0,0,0.15);
        }
        .simple-popup .leaflet-popup-content {
          margin: 0;
          line-height: 1.4;
        }
        .simple-popup .leaflet-popup-tip {
          background: white;
        }
        .pin-marker:hover {
          transform: scale(1.1);
        }
        /* Hide all Leaflet controls and attribution */
        .leaflet-control-container {
          display: none !important;
        }
        .leaflet-control-attribution {
          display: none !important;
        }
        .leaflet-control-zoom {
          display: none !important;
        }
        /* Disable map interactions */
        .leaflet-container {
          cursor: default !important;
        }
        .leaflet-container.leaflet-touch {
          cursor: default !important;
        }
      `}</style>
    </div>
  )
}
